import {Active, Over, useDndMonitor} from '@dnd-kit/core';
import {arrayMove} from '@dnd-kit/sortable';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {SaleVehiclePhotoType} from '@dms/api/settings';
import {getSortableContainerId} from '@dms/features/dnd';

import {PhotoGuideLayoutItem} from '../types/PhotoGuideLayoutItem';
import {UnifiedAlbumImage} from '../types/UnifiedAlbumImage';
import {getDndOverContainerId} from '../utils/getDndOverContainerId';
import {getPhotoGuideLayoutItemDndId} from '../utils/getPhotoGuideLayoutItemDndId';
import {parseSortableDndItemId, SortableDndItem} from '../utils/parseSortableDndItemId';
import {resolvePhotoGuideDropIntent} from '../utils/resolvePhotoGuideDropIntent';

interface UseGuidedAlbumDndManagerProps {
  /** The album id: the sortable container of the Backlog and the container in every tile id. */
  containerId: string;
  layoutContainerId: string;
  layout: PhotoGuideLayoutItem[];
  backlog: UnifiedAlbumImage[];
  inactiveContainerIds: string[];
  /** Photos selected in bulk mode across albums; a drag of one of them moves them all in. */
  selectedImageIds: string[];
  isEnabled: boolean;
  /** Photos from other albums may only be dropped in where the flat album would accept them. */
  canMovePhotosIn: boolean;
  onAssign: (saleVehiclePhotoId: string, photoType: SaleVehiclePhotoType) => void;
  onUnassign: (saleVehiclePhotoId: string) => void;
  onUpdateLayout: (layout: PhotoGuideLayoutItem[]) => void;
  onReorderBacklog: (backlog: UnifiedAlbumImage[], movedImage: UnifiedAlbumImage) => void;
  onMovePhotosIn: (sourceAlbumId: string, photoIds: string[], preferCopy: boolean) => void;
}

/** Where a dragged tile comes from, as seen from this album. */
type DragZone = 'layout' | 'backlog' | 'foreign';
type DropZone = 'layout' | 'backlog' | 'elsewhere';

/** A drop over a tile lands on that tile's index; a drop on the container's free area at its end. */
const getOverIndex = (overItem: SortableDndItem | null, listLength: number) =>
  isNil(overItem) ? listLength - 1 : overItem.position;

const insertAt = <T>(list: T[], index: number, item: T) => [
  ...list.slice(0, index),
  item,
  ...list.slice(index),
];

/**
 * Turns drops in a guided Sales Album into Guide Layout operations. Unlike the flat album
 * manager it keeps no ghost state: every drop is one call and the layout is refetched.
 *
 * - Backlog photo onto a slot: assign the slot's Photo Type.
 * - Backlog photo between layout tiles: insert as an Extra Photo.
 * - Layout tile over another layout tile: reorder (slots and Extra Photos alike).
 * - Filled slot or Extra Photo onto the Backlog: unassign.
 * - Photo from another album: move (or copy from a previous sale) into this album.
 */
export function useGuidedAlbumDndManager(props: UseGuidedAlbumDndManagerProps) {
  const toDragZone = (containerId: string): DragZone =>
    match(containerId)
      .returnType<DragZone>()
      .with(props.layoutContainerId, () => 'layout')
      .with(props.containerId, () => 'backlog')
      .otherwise(() => 'foreign');

  const toDropZone = (containerId: string | null): DropZone =>
    match(containerId)
      .returnType<DropZone>()
      .with(props.layoutContainerId, () => 'layout')
      .with(props.containerId, () => 'backlog')
      .otherwise(() => 'elsewhere');

  const findLayoutIndexByDndId = (active: Active) =>
    props.layout.findIndex(
      (item, index) => getPhotoGuideLayoutItemDndId(item, props.containerId, index) === active.id
    );

  const handleLayoutDrop = (active: Active, overItem: SortableDndItem | null) => {
    const activeIndex = findLayoutIndexByDndId(active);
    const overIndex = getOverIndex(overItem, props.layout.length);
    if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) {
      return;
    }
    props.onUpdateLayout(arrayMove(props.layout, activeIndex, overIndex));
  };

  const handleLayoutToBacklog = (active: Active) => {
    const activeLayoutItem = props.layout[findLayoutIndexByDndId(active)];
    if (isNotNil(activeLayoutItem?.image)) {
      props.onUnassign(activeLayoutItem.image.id);
    }
  };

  const handleBacklogToLayout = (
    image: UnifiedAlbumImage,
    active: Active,
    over: Over,
    overItem: SortableDndItem | null
  ) => {
    if (isNil(overItem)) {
      props.onUpdateLayout([...props.layout, {kind: 'extra', image}]);
      return;
    }
    const overLayoutItem = props.layout[overItem.position];
    if (isNil(overLayoutItem)) {
      return;
    }
    const intent = resolvePhotoGuideDropIntent(active, over);

    match({overLayoutItem, intent})
      .with({overLayoutItem: {kind: 'slot'}, intent: 'onto'}, ({overLayoutItem: slot}) =>
        props.onAssign(image.id, slot.photoType)
      )
      .with({intent: 'after'}, () =>
        props.onUpdateLayout(insertAt(props.layout, overItem.position + 1, {kind: 'extra', image}))
      )
      .with({intent: Pattern.union('before', 'onto')}, () =>
        props.onUpdateLayout(insertAt(props.layout, overItem.position, {kind: 'extra', image}))
      )
      .exhaustive();
  };

  const handleBacklogReorder = (
    image: UnifiedAlbumImage,
    activeIndex: number,
    overItem: SortableDndItem | null
  ) => {
    const overIndex = getOverIndex(overItem, props.backlog.length);
    if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) {
      return;
    }
    props.onReorderBacklog(arrayMove(props.backlog, activeIndex, overIndex), image);
  };

  // The dragged photo is at its own index in the Backlog; a stale id means the Backlog changed.
  const findBacklogImage = (activeItem: SortableDndItem) => {
    const image = props.backlog[activeItem.position];
    return isNotNil(image) && image.id === activeItem.id ? image : null;
  };

  const handleForeignDrop = (activeItem: SortableDndItem, activeContainerId: string) => {
    if (!props.canMovePhotosIn) {
      return;
    }
    const isBulkDrag =
      props.selectedImageIds.length > 1 && props.selectedImageIds.includes(activeItem.id);
    props.onMovePhotosIn(
      activeItem.containerId,
      isBulkDrag ? props.selectedImageIds : [activeItem.id],
      props.inactiveContainerIds.includes(activeContainerId)
    );
  };

  useDndMonitor({
    onDragEnd: ({active, over}) => {
      if (!props.isEnabled || isNil(over)) {
        return;
      }
      const activeItem = parseSortableDndItemId(active.id);
      if (isNil(activeItem)) {
        return;
      }
      const overItem = parseSortableDndItemId(over.id);
      const activeContainerId = getSortableContainerId(active) ?? activeItem.containerId;
      const zones = {
        from: toDragZone(activeContainerId),
        to: toDropZone(getDndOverContainerId(over)),
      };

      match(zones)
        .with({to: 'elsewhere'}, () => undefined)
        .with({from: 'foreign'}, () => handleForeignDrop(activeItem, activeContainerId))
        .with({from: 'layout', to: 'layout'}, () => handleLayoutDrop(active, overItem))
        .with({from: 'layout', to: 'backlog'}, () => handleLayoutToBacklog(active))
        .with({from: 'backlog'}, ({to}) => {
          const image = findBacklogImage(activeItem);
          if (isNil(image)) {
            return;
          }
          if (to === 'layout') {
            handleBacklogToLayout(image, active, over, overItem);
          } else {
            handleBacklogReorder(image, activeItem.position, overItem);
          }
        })
        .exhaustive();
    },
  });
}
